import React, { useEffect, useState } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { AtLimit, CharCounter } from '../components/ui/char-counter'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog'
import { Plus, Trash2, FolderKanban, Info, Pencil, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react'
import { generalWorkCategoryService, type GeneralWorkCategory } from '../services/generalWorkCategoryService'
import LoadingScreen from '../components/ui/LoadingScreen'
import { showSuccess, showError } from '../components/Alerts'
import { useAuth } from '../context/AuthContext'
import { PERMISSIONS } from '../constants/permissions'
import RequirePermission from '../components/RequirePermission'

const MAX_NAME = 50
const MAX_DESC = 200
const ITEMS_PER_PAGE = 10

const GeneralWorkCategoryConfig: React.FC = () => {
  const { hasPermission } = useAuth()
  const canEditConfig = hasPermission(PERMISSIONS.CONFIGURACION.EDITAR)

  const [rubros, setRubros] = useState<GeneralWorkCategory[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Nuevo rubro
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')

  // Edición
  const [editOpen, setEditOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const editingRubro = rubros.find(r => r.id === editingId) ?? null

  // Confirmación de eliminación
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [rubroToDelete, setRubroToDelete] = useState<GeneralWorkCategory | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Paginación
  const [page, setPage] = useState(1)

  const loadRubros = async () => {
    try {
      setIsLoading(true)
      const response = await generalWorkCategoryService.getAllGeneralWorkCategories()
      setRubros(response)
    } catch (e) {
      console.error(e)
      showError('Error al cargar los rubros generales')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRubros()
  }, [])

  // Paginación calculada
  const totalPages = Math.ceil(rubros.length / ITEMS_PER_PAGE)
  const safePage = Math.min(page, Math.max(1, totalPages))
  const pageItems = rubros.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE)

  const handleAddRubro = async () => {
    if (!canEditConfig) return
    const name = newName.trim()
    const desc = newDesc.trim()
    if (!name) return

    try {
      setIsLoading(true)
      const created = await generalWorkCategoryService.createGeneralWorkCategory(name, desc || '')
      setRubros(prev => [...prev, created])
      setNewName('')
      setNewDesc('')
      showSuccess('Rubro general creado exitosamente')
    } catch (e) {
      console.error(e)
      showError('Error al crear el rubro general')
    } finally {
      setIsLoading(false)
    }
  }

  const openDeleteConfirm = (rubro: GeneralWorkCategory) => {
    if (!canEditConfig || rubro.isSystemDefault) return
    setRubroToDelete(rubro)
    setDeleteOpen(true)
  }

  const handleDeleteRubro = async () => {
    if (!rubroToDelete) return
    try {
      setIsDeleting(true)
      await generalWorkCategoryService.deleteGeneralWorkCategory(rubroToDelete.id)
      setRubros(prev => prev.filter(r => r.id !== rubroToDelete.id))
      setDeleteOpen(false)
      setRubroToDelete(null)
      showSuccess('Rubro general eliminado exitosamente')
    } catch (e) {
      console.error(e)
      showError('Error al eliminar el rubro general')
    } finally {
      setIsDeleting(false)
    }
  }

  const openEdit = (r: GeneralWorkCategory) => {
    if (!canEditConfig || r.isSystemDefault) return
    setEditingId(r.id)
    setEditName(r.name ?? '')
    setEditDesc(r.description ?? '')
    setEditOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!canEditConfig || !editingId) return
    const name = editName.trim()
    const description = editDesc.trim()
    if (!name) return

    const prev = rubros
    setRubros(prev.map(r => r.id === editingId ? { ...r, name, description } : r))

    try {
      setIsLoading(true)
      await generalWorkCategoryService.updateGeneralWorkCategory(editingId, name, description)
      setEditOpen(false)
      setEditingId(null)
      showSuccess('Rubro general actualizado exitosamente')
    } catch (e) {
      console.error(e)
      showError('Error al actualizar el rubro general')
      setRubros(prev)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading && rubros.length === 0) {
    return <LoadingScreen message="Cargando rubros generales" />
  }

  return (
    <>
      {/* Info Banner */}
      <div className="px-6 py-3.5 border-b border-border bg-blue-50/60 dark:bg-blue-900/20">
        <div className="flex items-start gap-2.5">
          <Info className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Información:</strong> Los rubros marcados como "Sistema" son predeterminados y no
            pueden editarse ni eliminarse.
          </p>
        </div>
      </div>

      {/* Formulario de creación */}
      <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
        <div className="px-6 py-5 border-b border-border">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Plus className="w-4 h-4 text-primary" />
            Agregar nuevo rubro general
          </h3>
          <div className="p-4 bg-muted/30 border border-border rounded-lg space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-general-name" className="text-xs font-medium text-foreground sm:text-sm">
                  Nombre <span className="text-xs text-red-500">(Obligatorio)</span>
                </Label>
                <Input
                  id="new-general-name"
                  placeholder="Ingrese nombre del rubro"
                  value={newName}
                  onChange={e => { if (e.target.value.length <= MAX_NAME) setNewName(e.target.value) }}
                  disabled={isLoading}
                  maxLength={MAX_NAME}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={newName} max={MAX_NAME} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-general-desc" className="text-xs font-medium text-foreground sm:text-sm">
                  Descripción <span className="text-xs text-muted-foreground">(opcional)</span>
                </Label>
                <Textarea
                  id="new-general-desc"
                  placeholder="Descripción del rubro"
                  value={newDesc}
                  onChange={e => { if (e.target.value.length <= MAX_DESC) setNewDesc(e.target.value) }}
                  disabled={isLoading}
                  maxLength={MAX_DESC}
                  size="sm"
                  className="resize-none rounded-lg"
                />
                <CharCounter value={newDesc} max={MAX_DESC} />
              </div>
            </div>
            <Button
              onClick={handleAddRubro}
              disabled={isLoading || !newName.trim()}
              size="sm"
              className="h-9 px-5 rounded-lg"
              type="button"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Agregar Rubro
            </Button>
          </div>
        </div>
      </RequirePermission>

      {/* Lista de rubros */}
      <div className="px-6 py-5">
        {rubros.length === 0 ? (
          <div className="py-14 text-center">
            <FolderKanban className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground mb-1">No hay rubros generales configurados</p>
            <p className="text-xs text-muted-foreground">Agregá el primero usando el formulario de arriba</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {pageItems.map(rubro => (
                <div key={rubro.id} className="flex items-center gap-2">
                  <RequirePermission
                    permission={PERMISSIONS.CONFIGURACION.EDITAR}
                    fallback={
                      <div className={`flex-1 min-w-0 px-4 py-3 border rounded-lg ${
                        rubro.isSystemDefault ? 'bg-muted border-border opacity-70' : 'bg-card border-border'
                      }`}>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium truncate text-foreground">{rubro.name}</h4>
                          {rubro.isSystemDefault && (
                            <span className="px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded-full flex-shrink-0">Sistema</span>
                          )}
                        </div>
                        {rubro.description
                          ? <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rubro.description}</p>
                          : <p className="text-xs italic text-muted-foreground mt-0.5">Sin descripción</p>
                        }
                      </div>
                    }
                  >
                    <>
                      <button
                        type="button"
                        onClick={() => !rubro.isSystemDefault && openEdit(rubro)}
                        disabled={rubro.isSystemDefault}
                        className={`flex-1 min-w-0 px-4 py-3 text-left transition-colors border rounded-lg focus:outline-none ${
                          rubro.isSystemDefault
                            ? 'bg-muted border-border cursor-not-allowed opacity-70'
                            : 'bg-card border-border hover:bg-muted/50 focus:ring-2 focus:ring-primary'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium truncate text-foreground">{rubro.name}</h4>
                              {rubro.isSystemDefault && (
                                <span className="px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded-full flex-shrink-0">Sistema</span>
                              )}
                            </div>
                            {rubro.description
                              ? <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rubro.description}</p>
                              : <p className="text-xs italic text-muted-foreground mt-0.5">Sin descripción</p>
                            }
                          </div>
                          {!rubro.isSystemDefault && <Pencil className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                        </div>
                      </button>
                      <Button
                        onClick={() => openDeleteConfirm(rubro)}
                        disabled={isLoading || rubro.isSystemDefault}
                        variant="outline"
                        size="sm"
                        className={[
                          'h-9 w-9 flex-shrink-0 rounded-lg p-0',
                          rubro.isSystemDefault ? 'opacity-30 cursor-not-allowed' : 'hover:border-destructive hover:bg-destructive/10'
                        ].join(' ')}
                        title={rubro.isSystemDefault ? 'No se puede eliminar (rubro de sistema)' : 'Eliminar rubro'}
                        type="button"
                      >
                        <Trash2 className={rubro.isSystemDefault ? 'w-3.5 h-3.5 text-muted-foreground' : 'w-3.5 h-3.5 text-destructive'} />
                      </Button>
                    </>
                  </RequirePermission>
                </div>
              ))}
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Mostrando{' '}
                <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
                <span className="font-semibold text-foreground">{rubros.length}</span>{' '}
                rubro{rubros.length !== 1 ? 's' : ''}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1} className="rounded-lg">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-foreground tabular-nums">{safePage} / {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages} className="rounded-lg">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Dialog de edición */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-base font-semibold text-foreground">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <Pencil className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              Editar rubro general
            </DialogTitle>
            {editingRubro && (
              <DialogDescription className="text-muted-foreground">
                Modificando: <span className="font-medium text-foreground">{editingRubro.name}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-general-name" className="text-xs font-medium text-foreground sm:text-sm">
                Nombre <span className="text-xs text-red-500">(Obligatorio)</span>
              </Label>
              <Input
                id="edit-general-name"
                value={editName}
                onChange={e => { if (e.target.value.length <= MAX_NAME) setEditName(e.target.value) }}
                maxLength={MAX_NAME}
                placeholder="Nombre del rubro"
                autoFocus
                className="rounded-lg bg-background border-border focus-visible:ring-primary"
              />
              <AtLimit value={editName} max={MAX_NAME} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-general-desc" className="text-xs font-medium text-foreground sm:text-sm">Descripción</Label>
              <Textarea
                id="edit-general-desc"
                value={editDesc}
                onChange={e => { if (e.target.value.length <= MAX_DESC) setEditDesc(e.target.value) }}
                maxLength={MAX_DESC}
                placeholder="Descripción del rubro (opcional)"
                size="sm"
                className="resize-none rounded-lg"
              />
              <CharCounter value={editDesc} max={MAX_DESC} />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} type="button" className="rounded-lg">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button
                onClick={handleSaveEdit}
                disabled={isLoading || !editName.trim()}
                type="button"
                className="rounded-lg"
              >
                Guardar cambios
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar el rubro{' '}
              <span className="font-medium text-foreground">"{rubroToDelete?.name}"</span>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={isDeleting} className="rounded-lg">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button
                variant="destructive"
                onClick={handleDeleteRubro}
                disabled={isDeleting}
                className="rounded-lg"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default GeneralWorkCategoryConfig
